import React, { useState, useRef } from 'react';
import { 
  Camera, 
  X, 
  Check, 
  RotateCcw, 
  Send, 
  MapPin, 
  Clock, 
  User,
  Package,
  CheckCircle2,
  Crown,
  Shield,
  Image,
  AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

type DeliveryProvider = 'fash' | 'uber';
type DeliveryMethod = 'door' | 'meet' | 'reception';

interface CameraViewProps {
  orderId: string;
  provider?: DeliveryProvider;
  isPremium?: boolean;
  customerName?: string;
  deliveryMethod?: DeliveryMethod;
  onSubmit: (photoUrl: string, note?: string) => void;
  onCancel: () => void;
}

export function CameraView({ 
  orderId, 
  provider = 'fash',
  isPremium = false,
  customerName = 'Customer',
  deliveryMethod = 'door',
  onSubmit, 
  onCancel 
}: CameraViewProps) {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryMethod>(deliveryMethod);
  const [showDeliveryOptions, setShowDeliveryOptions] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const isUber = provider === 'uber';

  // Start camera
  React.useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
      setCameraError(null);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError('Unable to access camera. Please grant camera permissions.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      
      // Stop camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const handleSubmit = async () => {
    if (!capturedImage) return;
    
    setIsUploading(true);
    
    // Simulate upload - in real app, upload to Firebase Storage
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In production, this would be the actual uploaded URL
    onSubmit(capturedImage, note || undefined);
  };

  const deliveryOptions: { id: DeliveryMethod; label: string; description: string; icon: React.ReactNode }[] = [
    { 
      id: 'door', 
      label: 'Left at door', 
      description: 'Package left at customer\'s door',
      icon: <Package className="h-5 w-5" />
    },
    { 
      id: 'meet', 
      label: 'Handed to customer', 
      description: 'Delivered directly to recipient',
      icon: <User className="h-5 w-5" />
    },
    { 
      id: 'reception', 
      label: 'Left at reception', 
      description: 'Left with building staff',
      icon: <MapPin className="h-5 w-5" />
    },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Header */}
      <div className="absolute top-0 inset-x-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <button onClick={onCancel} className="p-2 text-white hover:bg-white/10 rounded-full transition-colors">
            <X className="h-6 w-6" />
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                isUber ? "bg-white text-black" : "bg-purple-500 text-white"
              )}>
                {isUber ? 'Uber' : 'Fash'}
              </span>
              {isPremium && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500 text-white flex items-center">
                  <Crown className="h-3 w-3 mr-1" />
                  Priority
                </span>
              )}
            </div>
            <p className="text-white font-medium">Delivery Proof</p>
            <p className="text-white/60 text-sm">Order #{orderId.slice(-6)}</p>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </div>

      {/* Camera View / Captured Image */}
      <div className="flex-1 relative">
        {cameraError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 p-6 text-center">
            <div>
              <div className={cn(
                "w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4",
                isUber ? "bg-white/10" : "bg-purple-900/50"
              )}>
                <Camera className="h-10 w-10 text-gray-500" />
              </div>
              <p className="text-white mb-4">{cameraError}</p>
              <Button 
                onClick={startCamera} 
                variant="outline" 
                className="text-white border-white hover:bg-white/10"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : capturedImage ? (
          <AnimatePresence>
            <motion.img
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={capturedImage}
              alt="Captured"
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
        )}

        {/* Viewfinder Overlay (when camera active) */}
        {!capturedImage && !cameraError && (
          <div className="absolute inset-0 pointer-events-none">
            <div className={cn(
              "absolute inset-8 border-2 rounded-3xl",
              isUber ? "border-white/40" : "border-purple-400/40"
            )} />
            <div className="absolute bottom-40 inset-x-0 text-center px-6">
              <div className={cn(
                "inline-flex items-center space-x-2 px-4 py-2 rounded-full",
                isUber ? "bg-white/20" : "bg-purple-500/30"
              )}>
                <Shield className="h-4 w-4 text-white" />
                <p className="text-white/90 text-sm">
                  {selectedDeliveryType === 'door' && 'Take a photo of the package at the door'}
                  {selectedDeliveryType === 'meet' && 'Take a photo for delivery confirmation'}
                  {selectedDeliveryType === 'reception' && 'Take a photo at the reception area'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Verified badge overlay on captured image */}
        {capturedImage && (
          <div className="absolute top-24 inset-x-0 flex justify-center">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-full",
                isUber ? "bg-white" : "bg-purple-500"
              )}
            >
              <CheckCircle2 className={cn("h-5 w-5", isUber ? "text-black" : "text-white")} />
              <span className={cn("font-medium", isUber ? "text-black" : "text-white")}>
                Photo captured
              </span>
            </motion.div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/90 to-transparent pt-12 pb-8 px-6">
        {capturedImage ? (
          <div className="space-y-4">
            {/* Delivery type selector */}
            <div>
              <button
                onClick={() => setShowDeliveryOptions(!showDeliveryOptions)}
                className="w-full flex items-center justify-between p-3 bg-white/10 rounded-xl border border-white/20"
              >
                <div className="flex items-center space-x-3">
                  {deliveryOptions.find(o => o.id === selectedDeliveryType)?.icon}
                  <span className="text-white font-medium">
                    {deliveryOptions.find(o => o.id === selectedDeliveryType)?.label}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: showDeliveryOptions ? 180 : 0 }}
                  className="text-white/60"
                >
                  ▼
                </motion.div>
              </button>
              
              <AnimatePresence>
                {showDeliveryOptions && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-2 overflow-hidden"
                  >
                    <div className="space-y-2">
                      {deliveryOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => {
                            setSelectedDeliveryType(option.id);
                            setShowDeliveryOptions(false);
                          }}
                          className={cn(
                            "w-full flex items-center space-x-3 p-3 rounded-xl transition-colors",
                            selectedDeliveryType === option.id
                              ? isUber ? "bg-white text-black" : "bg-purple-500 text-white"
                              : "bg-white/10 text-white hover:bg-white/20"
                          )}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            selectedDeliveryType === option.id
                              ? isUber ? "bg-gray-100" : "bg-purple-400"
                              : "bg-white/10"
                          )}>
                            {option.icon}
                          </div>
                          <div className="text-left">
                            <p className="font-medium">{option.label}</p>
                            <p className={cn(
                              "text-sm",
                              selectedDeliveryType === option.id
                                ? isUber ? "text-gray-600" : "text-purple-200"
                                : "text-white/60"
                            )}>
                              {option.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Note Input */}
            <Input
              placeholder="Add a note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
            />

            {/* Uber-specific: GPS verification indicator */}
            {isUber && (
              <div className="flex items-center space-x-2 text-emerald-400 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                <span>GPS location verified</span>
              </div>
            )}
            
            <div className="flex space-x-4">
              <Button
                variant="outline"
                onClick={retakePhoto}
                className="flex-1 border-white/30 text-white hover:bg-white/10"
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                Retake
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isUploading}
                className={cn(
                  "flex-1",
                  isUber ? "bg-white text-black hover:bg-gray-100" : "bg-success hover:bg-green-600"
                )}
              >
                <Send className="h-5 w-5 mr-2" />
                Complete Delivery
              </Button>
            </div>

            {/* Important notice */}
            <p className="text-center text-white/50 text-xs flex items-center justify-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              Photo will be shared with {customerName.split(' ')[0]} for confirmation
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quick delivery type selector before capture */}
            <div className="flex justify-center space-x-2">
              {deliveryOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedDeliveryType(option.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                    selectedDeliveryType === option.id
                      ? isUber ? "bg-white text-black" : "bg-purple-500 text-white"
                      : "bg-white/20 text-white/80 hover:bg-white/30"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Capture button */}
            <div className="flex justify-center">
              <button
                onClick={capturePhoto}
                disabled={!!cameraError}
                className={cn(
                  "w-20 h-20 rounded-full border-4 flex items-center justify-center transition-transform active:scale-95",
                  cameraError 
                    ? "opacity-50 border-gray-500" 
                    : isUber 
                      ? "border-white hover:scale-105" 
                      : "border-purple-400 hover:scale-105"
                )}
              >
                <div className={cn(
                  "w-16 h-16 rounded-full",
                  isUber ? "bg-white" : "bg-purple-400"
                )} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
